import { AuthError, createClient } from '@supabase/supabase-js';
import { getConfig, getEnvironment } from '../env';

const config = getConfig();
type AuthClient = ReturnType<typeof createClient>;
type AuthStateCallback = Parameters<AuthClient['auth']['onAuthStateChange']>[0];
type SignInResult = Awaited<ReturnType<AuthClient['auth']['signInWithPassword']>>;
const createAuthClient = (url: string, key: string): AuthClient => createClient(url, key, {
  auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true },
});

/**
 * The backend is the authority for which Supabase project serves each
 * deployment. Production and staging use different Supabase projects, so a
 * build-time staging key must never be reused by the production browser.
 * `/api/auth-config` returns only the browser-safe publishable key.
 */
async function resolveRuntimeClient(): Promise<AuthClient> {
  const resolvedConfig = { ...config };
  const environment = getEnvironment();
  if (typeof window !== 'undefined' && environment !== 'development') {
    try {
      const response = await fetch(`${config.apiBase}/api/auth-config`, {
        headers: { accept: 'application/json' },
      });
      if (response.ok) {
        const runtime = await response.json() as { enabled?: boolean; supabaseUrl?: string; anonKey?: string };
        if (runtime.enabled && runtime.supabaseUrl && runtime.anonKey) {
          resolvedConfig.supabaseUrl = runtime.supabaseUrl;
          resolvedConfig.supabasePublishableKey = runtime.anonKey;
        }
      }
    } catch { /* handled by the fail-closed validation below */ }
  }
  if (!resolvedConfig.supabaseUrl || !resolvedConfig.supabasePublishableKey) {
    throw new Error('Production authentication configuration is unavailable.');
  }
  if (environment === 'production') {
    const hostname = new URL(resolvedConfig.supabaseUrl).hostname;
    if (hostname !== 'gmdsprtbbqnjubbpytfc.supabase.co') {
      throw new Error('Production authentication configuration is invalid.');
    }
  }
  return createAuthClient(resolvedConfig.supabaseUrl, resolvedConfig.supabasePublishableKey);
}

async function signInThroughOzira(email: string, password: string): Promise<SignInResult> {
  const response = await fetch(`${config.apiBase}/api/auth/sign-in`, {
    method: 'POST',
    headers: { accept: 'application/json', 'content-type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  let payload: { error?: string; session?: Parameters<AuthClient['auth']['setSession']>[0] } | null = null;
  try { payload = await response.json(); } catch (_) {}
  if (!response.ok || !payload?.session) {
    return {
      data: { session: null, user: null },
      error: new AuthError(payload?.error || 'Could not sign in.'),
    };
  }
  const client = await runtimeClient();
  const result = await client.auth.setSession(payload.session);
  if (result.error || !result.data.user || !result.data.session) {
    return {
      data: { user: null, session: null },
      error: result.error || new AuthError('Could not establish the signed-in session.'),
    };
  }
  return { data: { user: result.data.user, session: result.data.session }, error: null };
}

let runtimeClientPromise: Promise<AuthClient> | null = null;
function runtimeClient(): Promise<AuthClient> {
  if (!runtimeClientPromise) runtimeClientPromise = resolveRuntimeClient();
  return runtimeClientPromise;
}

// Browser secure-session storage (spec section 17): Supabase's JS SDK
// persists the session in localStorage by default on Web, which is the
// standard browser-appropriate mechanism (equivalent in role to Keychain
// on iOS / Keystore on Android / a platform credential store on Desktop --
// see the mobile inventory's security note for why RN's storage choice is
// tracked separately as a P1, not fixed here).
// Keep the existing auth-shaped export so every screen continues to use the
// same client abstraction. Calls are deferred until the deployment-specific
// runtime configuration has been resolved.
export const supabase = {
  auth: {
    getSession: () => runtimeClient().then((client) => client.auth.getSession()),
    refreshSession: () => runtimeClient().then((client) => client.auth.refreshSession()),
    signInWithPassword: async (credentials: Parameters<AuthClient['auth']['signInWithPassword']>[0]) => {
      // Keep credential submission same-origin on hosted deployments. This
      // avoids provider requests being blocked or stalled by an embedded
      // browser while preserving Supabase's normal persisted session client.
      if ('email' in credentials && typeof window !== 'undefined' && getEnvironment() !== 'development') {
        try {
          return await signInThroughOzira(credentials.email, credentials.password);
        } catch (_) {
          // The direct Supabase path remains a safe compatibility fallback
          // for deployments whose same-origin auth route is unavailable.
        }
      }
      return runtimeClient().then((client) => client.auth.signInWithPassword(credentials));
    },
    signUp: (credentials: Parameters<AuthClient['auth']['signUp']>[0]) =>
      runtimeClient().then((client) => client.auth.signUp(credentials)),
    resend: (params: Parameters<AuthClient['auth']['resend']>[0]) =>
      runtimeClient().then((client) => client.auth.resend(params)),
    verifyOtp: (params: Parameters<AuthClient['auth']['verifyOtp']>[0]) =>
      runtimeClient().then((client) => client.auth.verifyOtp(params)),
    signInWithOAuth: (credentials: Parameters<AuthClient['auth']['signInWithOAuth']>[0]) =>
      runtimeClient().then((client) => client.auth.signInWithOAuth(credentials)),
    signOut: (options?: Parameters<AuthClient['auth']['signOut']>[0]) =>
      runtimeClient().then((client) => client.auth.signOut(options)),
    onAuthStateChange: (callback: AuthStateCallback): ReturnType<AuthClient['auth']['onAuthStateChange']> => {
      let disposed = false;
      let subscription: ReturnType<AuthClient['auth']['onAuthStateChange']> | null = null;
      void runtimeClient().then((client) => {
        if (!disposed) subscription = client.auth.onAuthStateChange(callback);
      });
      return {
        data: {
          subscription: {
            unsubscribe: () => {
              disposed = true;
              subscription?.data.subscription.unsubscribe();
            },
          },
        },
      } as ReturnType<AuthClient['auth']['onAuthStateChange']>;
    },
  },
} as Pick<AuthClient, 'auth'>;
